/**
 * Client-side validation for the self-serve campaign builder wizard (epic-10).
 *
 * Mirrors the backend submit-time compliance gate (services/selfserve.py) so the
 * partner gets early, per-step feedback BEFORE submitting:
 *   - skill challenge REQUIRED — winner decided by skill, NEVER a draw/lottery.
 *   - corporate prizes are NON-CASH (enforced server-side; the wizard never even
 *     offers a cash option).
 *   - a real prize: title, description, positive value.
 *
 * Kept framework-free so it can be unit-tested in isolation.
 */
import type { DraftCreatePayload } from './api';

export type BuilderValues = {
  // Step 1 — Brand & partner
  partner_id: string;
  name: string;
  brand_assets_url: string;
  // Step 2 — Prize
  prize_title: string;
  prize_description: string;
  prize_value_rand: string; // user enters in Rand; converted to cents on submit
  prize_currency: string;
  // Step 3 — Skill challenge
  skill_challenge_type: string;
  // Step 4 — Window & tracking
  starts_at: string; // yyyy-mm-dd
  ends_at: string;   // yyyy-mm-dd
  tracking_notes: string;
  // Step 5 — Consent & opt-ins
  leads_capture_enabled: boolean;
  ai_optin_enabled: boolean;
  consent_terms: boolean;
};

export const EMPTY_BUILDER: BuilderValues = {
  partner_id: '',
  name: '',
  brand_assets_url: '',
  prize_title: '',
  prize_description: '',
  prize_value_rand: '',
  prize_currency: 'ZAR',
  skill_challenge_type: '',
  starts_at: '',
  ends_at: '',
  tracking_notes: '',
  leads_capture_enabled: false,
  ai_optin_enabled: false,
  consent_terms: false,
};

export type BuilderErrors = Partial<Record<keyof BuilderValues, string>>;

// Fields grouped by wizard step (preview is the last index, no inputs).
export const BUILDER_STEP_FIELDS: (keyof BuilderValues)[][] = [
  ['partner_id', 'name', 'brand_assets_url'],
  ['prize_title', 'prize_description', 'prize_value_rand', 'prize_currency'],
  ['skill_challenge_type'],
  ['starts_at', 'ends_at', 'tracking_notes'],
  ['leads_capture_enabled', 'ai_optin_enabled', 'consent_terms'],
];

const URL_RE = /^https?:\/\/[^\s.]+\.[^\s]{2,}$/i;

/** Parse a Rand amount (e.g. "750,000" or "R750000") to integer cents, or null. */
export function randToCents(input: string): number | null {
  const cleaned = input.replace(/[^\d.]/g, '');
  if (!cleaned) return null;
  const rand = Number.parseFloat(cleaned);
  if (!Number.isFinite(rand) || rand <= 0) return null;
  return Math.round(rand * 100);
}

export function validateBuilderField(
  name: keyof BuilderValues,
  values: BuilderValues,
): string | undefined {
  const v = values[name];
  const str = typeof v === 'string' ? v.trim() : v;

  switch (name) {
    case 'partner_id':
      if (!str) return 'A partner is required.';
      return undefined;
    case 'name':
      if (!str) return 'Give your campaign a name.';
      return undefined;
    case 'brand_assets_url':
      if (str && !URL_RE.test(String(str)))
        return 'Enter a valid URL (e.g. https://example.com/logo.png).';
      return undefined;
    case 'prize_title':
      if (!str) return 'A prize title is required.';
      return undefined;
    case 'prize_description':
      if (!str) return 'A prize description is required.';
      return undefined;
    case 'prize_value_rand':
      if (!str) return 'Prize value is required.';
      if (randToCents(String(str)) === null)
        return 'Enter a positive prize value.';
      return undefined;
    case 'prize_currency':
      if (!str) return 'Currency is required.';
      return undefined;
    case 'skill_challenge_type':
      if (!str)
        return 'Choose a skill challenge — every competition is decided by skill, never a draw.';
      return undefined;
    case 'ends_at':
      if (values.starts_at && values.ends_at && values.ends_at < values.starts_at)
        return 'The end date must be on or after the start date.';
      return undefined;
    case 'consent_terms':
      if (v !== true)
        return 'Please confirm you have authority and understand the skill-based, consent-first terms.';
      return undefined;
    default:
      return undefined;
  }
}

export function validateBuilderStep(
  stepIndex: number,
  values: BuilderValues,
): BuilderErrors {
  const errors: BuilderErrors = {};
  for (const field of BUILDER_STEP_FIELDS[stepIndex] ?? []) {
    const err = validateBuilderField(field, values);
    if (err) errors[field] = err;
  }
  return errors;
}

export function validateBuilderAll(values: BuilderValues): BuilderErrors {
  const errors: BuilderErrors = {};
  BUILDER_STEP_FIELDS.forEach((_, i) =>
    Object.assign(errors, validateBuilderStep(i, values)),
  );
  return errors;
}

/** Build the draft-create payload from validated wizard values. */
export function toDraftPayload(values: BuilderValues): DraftCreatePayload {
  const cents = randToCents(values.prize_value_rand);
  return {
    partner_id: values.partner_id.trim(),
    name: values.name.trim(),
    brand_assets_url: values.brand_assets_url.trim() || undefined,
    prize_title: values.prize_title.trim() || undefined,
    prize_description: values.prize_description.trim() || undefined,
    prize_estimated_value_cents: cents ?? undefined,
    prize_currency: values.prize_currency.trim() || undefined,
    skill_challenge_type: values.skill_challenge_type || undefined,
    starts_at: values.starts_at ? `${values.starts_at}T00:00:00Z` : undefined,
    ends_at: values.ends_at ? `${values.ends_at}T23:59:59Z` : undefined,
    tracking_notes: values.tracking_notes.trim() || undefined,
    leads_capture_enabled: values.leads_capture_enabled,
    ai_optin_enabled: values.ai_optin_enabled,
  };
}
